import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { ListItem } from '../types/list';
import { HOTEL_ID } from './supabase/configClient';

class ListClient {
  private static instance: ListClient;

  private constructor() {}

  public static getInstance(): ListClient {
    if (!ListClient.instance) {
      ListClient.instance = new ListClient();
    }
    return ListClient.instance;
  }

  public async getItems(sectionId: string, sortType?: string): Promise<ListItem[]> {
    try {
      // Vérifier les paramètres requis
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      // Charger les items, traductions et configuration de la section en parallèle
      const [itemsResponse, descriptionsResponse, offersResponse, sectionResponse] = await Promise.all([
        supabaseClient
          .from('hotels_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),

        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'description'),

        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'offer'),

        supabaseClient
          .from('hotels_navigation')
          .select('filter_open_section')
          .eq('id', sectionId)
          .single()
      ]);

      if (itemsResponse.error) {
        throw itemsResponse.error;
      }

      if (descriptionsResponse.error) {
        throw descriptionsResponse.error;
      }

      const langs = ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'] as const;

      const descriptionsMap = new Map();
      descriptionsResponse.data.forEach(translation => {
        const t: Record<string, string> = {};
        for (const lang of langs) {
          if (translation[lang]) t[lang] = translation[lang];
        }
        if (translation.item_id) descriptionsMap.set(translation.item_id, t);
      });

      const offersMap = new Map();
      if (offersResponse.data) {
        offersResponse.data.forEach(translation => {
          const t: Record<string, string> = {};
          for (const lang of langs) {
            if (translation[lang]) t[lang] = translation[lang];
          }
          if (translation.item_id) offersMap.set(translation.item_id, t);
        });
      }

      const items = itemsResponse.data.map(record => {
        const descriptions = descriptionsMap.get(record.id) || {};
        const offers = offersMap.get(record.id) || {};

        return {
          id: record.id,
          name: record.name,
          descriptions,
          description: record.description,
          offers,
          offer: record.offer || '',
          fulladress: record.fulladress || '',
          telephone: record.telephone || '',
          url: record.url || '',
          category: record.category || 'Autre',
          type: record.type || '',
          image: record.image_metadata?.url || '',
          google_map: record.google_map || '',
          appstore: record.appstore || '',
          playstore: record.playstore || ''
        };
      });

      // Utiliser le tri initial de la section si aucun tri n'est spécifié
      const initialSortType = !sortType && sectionResponse.data?.filter_open_section;
      const effectiveSortType = sortType || initialSortType;

      // Sort items if needed
      const sortedItems = this.sortItems(items, effectiveSortType);

      return sortedItems;

    } catch (error) {
      logger.error('Error fetching list items', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  private sortItems(items: ListItem[], sortType?: string): ListItem[] {
    switch (sortType) {
      case 'shuffle':
        return [...items].sort(() => Math.random() - 0.5);
      
      case 'name':
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
      
      case 'category':
        return [...items].sort((a, b) => {
          const catCompare = a.category.localeCompare(b.category);
          if (catCompare !== 0) return catCompare;
          return a.name.localeCompare(b.name);
        });
      
      default:
        return items;
    }
  }
}

const listClient = ListClient.getInstance();

export { listClient };